import React from 'react';
import { ChevronDown } from 'lucide-react';
import { USER_IMAGES } from '../../constants';

// Hero Section
export const HeroSection = ({ lightSettings }) => {
  const { enabled, speed, intensity } = lightSettings;
  
  // Calculate animation duration based on speed
  const getDuration = () => {
    switch (speed) {
      case 'slow': return '12s';
      case 'fast': return '4s';
      default: return '8s';
    }
  };
  
  // Calculate size based on intensity
  const getSize = (base) => {
    switch (intensity) {
      case 'low': return base * 0.6;
      case 'high': return base * 1.4;
      default: return base;
    }
  };
  
  const getBlur = () => {
    switch (intensity) {
      case 'low': return 'blur-md';
      case 'high': return 'blur-2xl';
      default: return 'blur-xl';
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={USER_IMAGES.logo1}
          alt="JasnéSvetlá"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05050A] via-[#05050A]/90 to-[#05050A]/50" />
      </div>

      {/* Flickering Headlight Effect - Conditionally rendered */}
      {enabled && (
        <>
          <div className="absolute top-1/3 right-1/4 pointer-events-none">
            <div className="relative">
              {/* Main light glow */}
              <div 
                className={`rounded-full bg-[#00BFFF] ${getBlur()}`}
                style={{
                  width: `${getSize(32)}px`,
                  height: `${getSize(32)}px`,
                  animation: `headlightFlicker ${getDuration()} ease-in-out infinite`
                }}
              />
              {/* Light beam */}
              <div 
                className="absolute top-1/2 left-full h-1 bg-gradient-to-r from-[#00BFFF]/60 to-transparent"
                style={{
                  animation: `headlightBeam ${getDuration()} ease-in-out infinite`
                }}
              />
            </div>
          </div>
          <div className="absolute top-1/2 right-1/3 pointer-events-none">
            <div className="relative">
              <div 
                className={`rounded-full bg-white ${intensity === 'low' ? 'blur-sm' : intensity === 'high' ? 'blur-xl' : 'blur-lg'}`}
                style={{
                  width: `${getSize(24)}px`,
                  height: `${getSize(24)}px`,
                  animation: `headlightFlicker ${getDuration()} ease-in-out infinite`,
                  animationDelay: '3s'
                }}
              />
            </div>
          </div>
          {/* Additional light for high intensity */}
          {intensity === 'high' && (
            <div className="absolute top-2/5 right-1/5 pointer-events-none">
              <div 
                className="rounded-full bg-[#00BFFF]/50 blur-2xl"
                style={{
                  width: '20px',
                  height: '20px',
                  animation: `headlightFlicker ${getDuration()} ease-in-out infinite`,
                  animationDelay: '5s'
                }}
              />
            </div>
          )}
        </>
      )}

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00BFFF] animate-fadeInUp">
              Profesionálna starostlivosť o vaše auto
            </p>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light animate-fadeInUp stagger-1">
              Rozsvietime 
              <span className="block font-medium">vaše auto</span>
              <span className="text-[#00BFFF]">zvonku aj zvnútra</span>
            </h1>

            <p className="text-lg text-zinc-400 max-w-lg animate-fadeInUp stagger-2">
              Špecializujeme sa na renováciu svetlometov, montáž LED osvetlenia, 
              ambientné osvetlenie a hviezdny strop. Kvalita, ktorú vidíte.
            </p>

            <div className="flex flex-wrap gap-4 animate-fadeInUp stagger-3">
              <a
                href="#booking"
                data-testid="hero-cta-primary"
                className="btn-primary btn-glow"
              >
                Rezervovať termín
              </a>
              <a
                href="#services"
                data-testid="hero-cta-secondary"
                className="btn-secondary"
              >
                Zistite viac
              </a>
            </div>

            {/* Stats */}
            <div className="flex gap-8 pt-8 border-t border-white/10 animate-fadeInUp stagger-4">
              <div>
                <p className="text-3xl font-bold text-[#00BFFF]">200+</p>
                <p className="text-sm text-zinc-500">Spokojných zákazníkov</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#00BFFF]">5+</p>
                <p className="text-sm text-zinc-500">Rokov skúseností</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#00BFFF]">100%</p>
                <p className="text-sm text-zinc-500">Spokojnosť</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={32} className="text-[#00BFFF]" />
      </div>
    </section>
  );
};
